import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { useLang } from '../context/LanguageContext';

const Offers = () => {
  const { t, localize } = useLang();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);

  useEffect(() => {
    api.get('/promotions').then(({ data }) => setOffers(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (highlightId && offers.length > 0 && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [highlightId, offers]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-[#04140e]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c5a059]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf6ec]">
      <section className="pt-32 pb-16 bg-[#04140e] text-[#f5e9ce] text-center">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center space-x-3 mb-6">
            <span className="h-px w-12 bg-[#c5a059]"></span>
            <span className="text-[#c5a059] text-xs tracking-[0.3em]">{t.offers.exclusive}</span>
            <span className="h-px w-12 bg-[#c5a059]"></span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-serif font-bold mb-4 text-[#f5e9ce]">{t.offers.pageTitle}</h1>
          <p className="text-[#f5e9ce]/70 max-w-2xl mx-auto">{t.offers.pageSubtitle}</p>
        </div>
      </section>

      <section className="py-16 bg-[#faf6ec]">
        <div className="max-w-7xl mx-auto px-6">
          {offers.length === 0 ? (
            <div className="text-center py-20 text-[#0d3b25]/50">No offers available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {offers.map((offer) => {
                const savings = offer.total_price - offer.offer_price;
                const pct = Math.round((savings / offer.total_price) * 100);
                return (
                  <div
                    key={offer.id}
                    ref={offer.id === highlightId ? highlightRef : null}
                    data-testid={`offer-card-${offer.id}`}
                    className={`relative bg-white rounded-sm p-8 flex flex-col hover:shadow-2xl transition-all ${
                      offer.id === highlightId
                        ? 'border-2 border-[#c5a059] shadow-2xl ring-4 ring-[#c5a059]/30 scale-[1.02]'
                        : 'border border-[#c5a059]/30 hover:border-[#c5a059]'
                    }`}
                  >
                    {offer.id === highlightId && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c5a059] text-[#04140e] px-4 py-1 text-[10px] tracking-widest font-bold rounded-full">
                        YOUR SELECTED DEAL
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <Sparkles className="w-6 h-6 text-[#c5a059]" />
                      {pct > 0 && (
                        <span className="bg-[#c5a059] text-[#04140e] text-xs font-bold px-3 py-1 rounded-full">
                          -{pct}%
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-[#0d3b25] mb-3 leading-snug">
                      {localize(offer, 'title')}
                    </h3>
                    {localize(offer, 'description') && (
                      <p className="text-sm text-[#0d3b25]/70 mb-4 leading-relaxed" data-testid={`offer-desc-${offer.id}`}>
                        {localize(offer, 'description')}
                      </p>
                    )}
                    <div className="space-y-1 mb-6 text-sm text-[#0d3b25]/60">
                      <p className="uppercase text-[10px] tracking-widest text-[#c5a059] font-semibold mb-2">
                        {t.offers.services}
                      </p>
                      {(offer.services || []).map((svc, i) => (
                        <div key={i} className="flex items-center">
                          <span className="w-1.5 h-1.5 bg-[#c5a059] rounded-full mr-2"></span>
                          {localize(svc, 'name')}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-baseline space-x-3 mb-4">
                      <span className="text-[#0d3b25]/40 line-through">€{offer.total_price.toFixed(0)}</span>
                      <span className="text-[#c5a059] font-serif font-bold text-4xl">€{offer.offer_price.toFixed(0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#0d3b25]/50 mb-6">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {offer.duration} {t.common.min}
                      </span>
                      {offer.staff && (
                        <span>{t.common.with} <strong className="text-[#c5a059]">{offer.staff.name}</strong></span>
                      )}
                    </div>
                    <Link to={`/book?promo=${offer.id}`} className="mt-auto">
                      <Button className="w-full bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold">
                        {t.offers.bookThisDeal}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Offers;
